import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

const initialState = {
  notifications: [
    {
      id: 1,
      type: 'info',
      title: 'Welcome to Mom N Kidz SocialFlow',
      message: 'Your social media management platform is ready!',
      timestamp: new Date().toISOString(),
      isRead: false
    }
  ],
  unreadCount: 1,
  socket: null,
  connected: false
};

const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'SOCKET_CONNECT':
      return {
        ...state,
        socket: action.payload,
        connected: true
      };
    case 'SOCKET_DISCONNECT':
      return {
        ...state,
        socket: null,
        connected: false
      };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1
      };
    case 'MARK_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, isRead: true }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
    case 'MARK_ALL_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification => ({
          ...notification,
          isRead: true
        })),
        unreadCount: 0
      };
    case 'LOAD_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.isRead).length
      };
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
        unreadCount: 0
      };
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const socketRef = useRef(null);
  const { token } = useAuth();

  useEffect(() => {
    // Only connect when authenticated
    if (!token) {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          if (socketRef.current.connected) {
            socketRef.current.disconnect();
          }
        } catch (error) {
          // Silently handle disconnect errors
          console.debug('Socket disconnect error (safe to ignore):', error);
        }
        socketRef.current = null;
      }
      return;
    }

    if (socketRef.current) return; // avoid duplicate connections (e.g., StrictMode)

    const apiBase = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
      : window.location.origin;

    const socket = io(apiBase, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to notification server');
      dispatch({ type: 'SOCKET_CONNECT', payload: socket });
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from notification server');
      dispatch({ type: 'SOCKET_DISCONNECT' });
    });

    socket.on('notification', (notification) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      
      // Show toast notification
      toast(notification.message, {
        icon: getNotificationIcon(notification.type),
        duration: 5000,
      });
    });

    socket.on('post_published', (data) => {
      const notification = {
        id: Date.now(),
        type: 'success',
        title: 'Post Published',
        message: `Your post has been published on ${data.platform}`,
        timestamp: new Date().toISOString(),
        isRead: false
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      toast.success(notification.message);
    });

    socket.on('post_failed', (data) => {
      const notification = {
        id: Date.now(),
        type: 'error',
        title: 'Post Failed',
        message: `Failed to publish post on ${data.platform}: ${data.error}`,
        timestamp: new Date().toISOString(),
        isRead: false
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      toast.error(notification.message);
    });

    socket.on('team_mention', (data) => {
      const notification = {
        id: Date.now(),
        type: 'info',
        title: 'Team Mention',
        message: `You were mentioned in a post by ${data.user}`,
        timestamp: new Date().toISOString(),
        isRead: false
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      toast(notification.message, { icon: '👥' });
    });

    return () => {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          if (socketRef.current.connected) {
            socketRef.current.disconnect();
          }
        } catch (error) {
          // Silently handle disconnect errors (connection may already be closed)
          console.debug('Socket disconnect error (safe to ignore):', error);
        }
        socketRef.current = null;
      }
    };
  }, [token]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'mention':
        return '👥';
      default:
        return '📢';
    }
  };

  const markAsRead = (notificationId) => {
    dispatch({ type: 'MARK_READ', payload: notificationId });
  };

  const markAllAsRead = () => {
    dispatch({ type: 'MARK_ALL_READ' });
  };

  const clearNotifications = () => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  };

  const joinBrandRoom = (brandId) => {
    if (state.socket) {
      state.socket.emit('join-brand', brandId);
    }
  };

  const leaveBrandRoom = (brandId) => {
    if (state.socket) {
      state.socket.emit('leave-brand', brandId);
    }
  };

  const value = {
    ...state,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    joinBrandRoom,
    leaveBrandRoom
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};




